import { Node, mergeAttributes } from "@tiptap/core";
import {
  CommandProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";
import { Typography } from "antd-v5";
import axios from "axios";
import { FileIcon } from "components/Icons";
import dayjs from "dayjs";
import { compact, isString } from "lodash";
import { Plugin, PluginKey } from "prosemirror-state";
import React, { useEffect, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import {
  getFileSizeString,
  removeNonPrintableUnicodeCharacters,
  validateFileForUpload,
} from "utils/fileUtils";

import LoadingIcon from "ac-components/ac-icons/LoadingIcon";

import "./FileExtension.less";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    file: {
      insertFileAttachment: (file: File) => ReturnType;
    };
  }
}

const FileExtension = Node.create({
  name: "file-attachment",
  group: "block",
  atom: true,
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("eventHandler"),
        props: {
          handlePaste: (view, event, slice) => {
            // this is to prevent if a file is copied from a computer and pasted, then it pastes the file as well as its thumbnail
            // if an image is copied from a doc file along with other texts then it won't be a file
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            let file = getFilesFromClipboardEvent(event);
            file = file ? validateFileForUpload([file]) : undefined;
            if (!file || file.type.indexOf("image") > -1) {
              // Image extenstion is separate
              return;
            }
            this.editor.chain().focus().insertFileAttachment(file).run();
            this.editor.commands.setTextSelection(
              this.editor.state.selection.$from.pos + 2,
            );
          },
        },
      }),
    ];
  },
  // @ts-ignore
  addCommands() {
    return {
      insertFileAttachment:
        (file: File) =>
        ({ state, dispatch }: CommandProps) => {
          // Create the file-attachment node
          const fileAttachmentNode = this.type.create({ src: file });

          // Insert the file attachment node
          const transaction = state.tr.replaceSelectionWith(fileAttachmentNode);

          // Add a line break after the file-attachment
          const lineBreakNode = state.schema.nodes.paragraph.create();
          const newTransaction = transaction.insert(
            transaction.selection.$to.pos,
            lineBreakNode,
          );

          // Dispatch the transaction
          dispatch!(newTransaction);
        },
    };
  },
  addAttributes() {
    return {
      src: {},
      size: {},
      name: {},
      timestamp: {},
    };
  },
  parseHTML() {
    return [
      {
        tag: "file-attachment",
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["file-attachment", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(FileNodeView);
  },
});

export default FileExtension;

// Exported for testing
export const FileNodeView = (props: any) => {
  const [loading, setLoading] = useState(false);

  const {
    src,
    name,
    size,
    timestamp,
  }: { src: string | File; name?: string; size?: string; timestamp?: number } =
    props.node.attrs;

  const fileName = src instanceof File ? src.name : name || "";
  useEffect(() => {
    if (src instanceof File) {
      const file = src;
      const upload = async () => {
        const fileName = removeNonPrintableUnicodeCharacters(file.name);
        const { id, signedUrl } = await FindingAPI.uploadManualAttachment({
          fileName,
        }).promise;
        await axios({ method: "put", url: signedUrl, data: file });
        const params = new URLSearchParams({
          attachmentId: id,
          filename: fileName,
        });
        return `/user/tools/generic/CUSTOM/attachment?${params.toString()}`;
      };
      setLoading(true);
      upload()
        .then((src) => {
          props.updateAttributes({
            src,
            name: file.name,
            size: getFileSizeString(file.size),
            timestamp: Date.now(),
          });
        })
        .catch(() => {
          // Error toast already shown by makeCancellableAPIRequest/failSafe
          props.deleteNode();
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // to avoid setting updateAttributes to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const fileNameWithExtensionArr = fileName.split(".");
  const extension = fileNameWithExtensionArr.pop();
  const _fileName = fileNameWithExtensionArr.join(".");

  const content = (
    <div className="tip-tap-file-attachment">
      <div className="tip-tap-file-attachment-thumbnail">
        <FileIcon />
      </div>
      <div className="tip-tap-file-attachment-metadata text-ellipsis">
        {isString(src) && !loading ? (
          <Typography.Link className="w-100" href={src} target="_blank">
            <div
              className="file-attachment-name text-ellipsis"
              title={_fileName}
            >
              {_fileName}
            </div>
          </Typography.Link>
        ) : (
          <div
            className="file-attachment-name text-ellipsis w-100"
            title={_fileName}
          >
            {_fileName}
          </div>
        )}
        <div className="file-attachment-info">
          <div>
            {loading ? (
              <LoadingIcon />
            ) : (
              `${compact([size || undefined, extension ? extension.toUpperCase() : undefined]).join(" - ")}`
            )}
          </div>
          <div>
            {loading || !timestamp
              ? ""
              : dayjs(timestamp).format("MMM D, YYYY")}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <NodeViewWrapper>
      <div>{content}</div>
    </NodeViewWrapper>
  );
};

function getFilesFromClipboardEvent(e: ClipboardEvent): File | undefined {
  const items = e.clipboardData?.items;
  const files: File[] = [];
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const file = items[i].getAsFile();
      if (!file) continue;
      files.push(file);
    }
  }

  return files[0] || undefined;
}
